import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchWithAuth, condoApi } from '../services/api';

/**
 * "Pick a person by address" — the flow VisitorRegistrationModal has always got right, extracted
 * so the other screens that need it stop reimplementing it (and stop reimplementing it wrongly).
 *
 * THE RULE THAT MATTERS: condo-manager only applies `unit_id` inside `if roleType != ""`
 * (person_handler.go:188). A request that sends unit_id WITHOUT role_type has its filter silently
 * ignored and comes back with every person in the customer — no error, just wrong data. So
 * role_type is always sent alongside unit_id here, and the tests assert the parameters rather than
 * only the result.
 *
 * For a condomínio the roles are residents filtered by unit; for an empresa they are staff
 * filtered by department. Street/número exist only for casa-type condos and select a unit (and its
 * quadra) directly.
 */

export interface AddressPickerPerson {
  id: string;
  first_name: string;
  last_name: string;
  cpf?: string;
}

export interface AddressPickerBlock {
  id: string;
  name: string;
}

export interface AddressPickerUnit {
  id: string;
  number: string;
  block_id?: string;
  street_number?: string | null;
  street?: { id?: string; name?: string } | null;
}

export interface AddressPickerStreet {
  id: string;
  name: string;
  is_active?: boolean;
}

export interface AddressPickerDepartment {
  id: string;
  name: string;
}

export interface UseResidentAddressPickerOptions {
  customerId: string | null;
  customerType?: string;
  /** Skip every request — for screens that mount the hook before the picker is shown. */
  enabled?: boolean;
  /** Debounce for the typed search. */
  debounceMs?: number;
}

/**
 * Natural (numeric-aware) comparison. Quadras, lotes and house numbers are numbers stored as
 * text, so a plain sort puts "10" before "2" and "100" before "20".
 */
const naturalCompare = (a: string, b: string): number =>
  a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' });

const fullName = (p: AddressPickerPerson): string => `${p.first_name} ${p.last_name}`.trim();

const unwrap = <T,>(res: any): T[] => res?.data?.data || res?.data || [];

export function useResidentAddressPicker({
  customerId,
  customerType,
  enabled = true,
  debounceMs = 400,
}: UseResidentAddressPickerOptions) {
  const isCompany = customerType === 'empresa' || customerType === 'fabrica';
  // The role that owns an address: residents live in units, staff belong to departments.
  const roleType = isCompany ? 'staff' : 'resident';

  const [blocks, setBlocks] = useState<AddressPickerBlock[]>([]);
  const [units, setUnits] = useState<AddressPickerUnit[]>([]);
  const [streets, setStreets] = useState<AddressPickerStreet[]>([]);
  const [allCustomerUnits, setAllCustomerUnits] = useState<AddressPickerUnit[]>([]);
  const [departments, setDepartments] = useState<AddressPickerDepartment[]>([]);

  const [blockId, setBlockIdState] = useState('');
  const [unitId, setUnitId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [streetId, setStreetIdState] = useState('');
  const [streetNumber, setStreetNumberState] = useState('');
  const [search, setSearch] = useState('');

  const [residents, setResidents] = useState<AddressPickerPerson[]>([]);
  const [loadingResidents, setLoadingResidents] = useState(false);

  const active = enabled && !!customerId;

  useEffect(() => {
    if (!active || isCompany) return;
    condoApi.getBlocks(customerId as string)
      .then((res: any) =>
        setBlocks(unwrap<AddressPickerBlock>(res).sort((a, b) => naturalCompare(a.name || '', b.name || ''))))
      .catch(() => setBlocks([]));
  }, [active, customerId, isCompany]);

  // Streets are always attempted; a non-empty list is what marks a casa-type condo.
  useEffect(() => {
    if (!active || isCompany) return;
    condoApi.getStreets(customerId as string)
      .then((res: any) =>
        setStreets(
          unwrap<AddressPickerStreet>(res)
            .filter(st => st.is_active !== false)
            .sort((a, b) => naturalCompare(a.name || '', b.name || '')),
        ))
      .catch(() => setStreets([]));
  }, [active, customerId, isCompany]);

  useEffect(() => {
    if (!active || !isCompany) return;
    condoApi.getDepartments(customerId as string)
      .then((res: any) =>
        setDepartments(unwrap<AddressPickerDepartment>(res).sort((a, b) => naturalCompare(a.name || '', b.name || ''))))
      .catch(() => setDepartments([]));
  }, [active, customerId, isCompany]);

  useEffect(() => {
    if (!blockId) {
      setUnits([]);
      return;
    }
    condoApi.getUnits(blockId)
      .then((res: any) =>
        setUnits(unwrap<AddressPickerUnit>(res).sort((a, b) => naturalCompare(a.number || '', b.number || ''))))
      .catch(() => setUnits([]));
  }, [blockId]);

  // One street spans several quadras, so filtering by street needs every unit of the customer.
  useEffect(() => {
    if (!streetId || !customerId) {
      setAllCustomerUnits([]);
      return;
    }
    fetchWithAuth(`/api/v1/customers/${customerId}/units`)
      .then(async r => (r.ok ? r.json() : []))
      .then((data: any) => setAllCustomerUnits(Array.isArray(data) ? data : data?.data || []))
      .catch(() => setAllCustomerUnits([]));
  }, [streetId, customerId]);

  const streetNumbers = useMemo(() => {
    if (!streetId) return [] as string[];
    const onStreet = allCustomerUnits.filter(u => u.street?.id === streetId);
    return Array.from(new Set(onStreet.map(u => (u.street_number || '').trim()).filter(Boolean)))
      .sort(naturalCompare);
  }, [allCustomerUnits, streetId]);

  const setBlockId = useCallback((next: string) => {
    setBlockIdState(next);
    // Keeping the old unit would filter by an address that no longer matches the block shown
    // beside it.
    setUnitId('');
  }, []);

  const setStreetId = useCallback((next: string) => {
    setStreetIdState(next);
    setStreetNumberState('');
    setUnitId('');
  }, []);

  /** Picking street + número IS picking the unit; the quadra follows from it. */
  const setStreetNumber = useCallback((next: string) => {
    setStreetNumberState(next);
    if (!next) {
      setUnitId('');
      return;
    }
    const match = allCustomerUnits.find(
      u => u.street?.id === streetId && (u.street_number || '').trim() === next,
    );
    if (match) {
      if (match.block_id) setBlockIdState(match.block_id);
      setUnitId(match.id);
    }
  }, [allCustomerUnits, streetId]);

  const fetchResidents = useCallback(async (term: string, unit: string, department: string) => {
    const trimmed = term.trim();
    const hasScope = !!unit || !!department;
    // A chosen unit/department is a search on its own. Without one, two characters are required or
    // every keystroke would ask the server for the whole customer.
    if (!hasScope && trimmed.length < 2) {
      setResidents([]);
      return;
    }

    setLoadingResidents(true);
    try {
      const params = new URLSearchParams({
        // role_type is NOT optional: it is what makes the backend honour unit_id at all.
        role_type: roleType,
        include_inactive_roles: 'false',
        page_size: '1000',
      });
      if (trimmed) params.append('search', trimmed);
      if (unit && roleType === 'resident') params.append('unit_id', unit);
      if (department && roleType === 'staff') params.append('department_id', department);

      const res = await fetchWithAuth(`/api/v1/customers/${customerId}/people?${params.toString()}`);
      if (!res.ok) {
        setResidents([]);
        return;
      }
      const data = await res.json();
      const list: AddressPickerPerson[] = data?.data || data || [];
      // Dedupe by id: the unit filter is a hybrid over person_roles AND unit_residents
      // (person_handler.go), so someone present in both comes back twice and renders as two
      // identical rows nobody can tell apart.
      const unique = Array.from(new Map(list.map(p => [p.id, p])).values());
      setResidents(unique.sort((a, b) => naturalCompare(fullName(a), fullName(b))));
    } catch {
      setResidents([]);
    } finally {
      setLoadingResidents(false);
    }
  }, [customerId, roleType]);

  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => { void fetchResidents(search, unitId, departmentId); }, debounceMs);
    return () => clearTimeout(timer);
  }, [active, search, unitId, departmentId, fetchResidents, debounceMs]);

  /**
   * The rendered list. The server already filters, but behind a debounce and a round trip — with a
   * unit's worth of people on screen that reads as the search being ignored, so the typed term
   * also narrows locally on every keystroke.
   */
  const visibleResidents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return residents;
    const digits = term.replace(/\D/g, '');
    return residents.filter(p => {
      if (fullName(p).toLowerCase().includes(term)) return true;
      return !!digits && !!p.cpf && p.cpf.replace(/\D/g, '').includes(digits);
    });
  }, [residents, search]);

  const reset = useCallback(() => {
    setBlockIdState('');
    setUnitId('');
    setDepartmentId('');
    setStreetIdState('');
    setStreetNumberState('');
    setSearch('');
    setResidents([]);
  }, []);

  return {
    // options
    blocks,
    units,
    streets,
    departments,
    streetNumbers,
    hasStreets: streets.length > 0,
    isCompany,
    // selection
    blockId,
    unitId,
    departmentId,
    streetId,
    streetNumber,
    search,
    setBlockId,
    setUnitId,
    setDepartmentId,
    setStreetId,
    setStreetNumber,
    setSearch,
    // results
    residents,
    visibleResidents,
    loadingResidents,
    reset,
  };
}
